import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { cloneElement, type FC, type ReactElement } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form@latest';

import { _isNotEmpty, _notNil } from '@/littledash';

import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';

import { matchType, Type } from '@/referenceData/study/type';

import { MetadataCondition } from '@/components/Glossary/Sections/Approvals/TemplateForm/MetadataCondition';
import { studyTypeOptions } from '@/components/Glossary/Sections/Approvals/TemplateForm/TemplateForm.utils';
import Radio from '@/components/UI/FormFields/Latest/Radio';
import DraggableList, { asListItem } from '@/components/UI/List/DraggableList';

interface ReviewTemplateItem {
  id: ID;
  name: string;
}

interface MetadataConditionEntry {
  glossary_id?: ID | null;
  operation?: string;
  value?: unknown;
}

interface ApprovalLike {
  id?: ID;
  name?: string;
  study_type?: string;
  match_type?: string;
  conditions?: Array<MetadataConditionEntry>;
  dependencies?: ID | '' | null;
}

interface GeneralFormData {
  name: string;
  study_type: string;
  match_type: string;
  conditions: Array<MetadataConditionEntry>;
  dependencies: number | '' | undefined;
}

interface GeneralProps {
  ActionButtons: ReactElement<{ onOk?: () => void }>;
  onSubmit: (data: GeneralFormData) => void;
  approval: ApprovalLike;
  studyMetadataOptions: Array<MetadataField>;
  projectMetadataOptions: Array<MetadataField>;
  reviewTemplates: Array<ReviewTemplateItem>;
}

const General: FC<GeneralProps> = ({
  ActionButtons,
  onSubmit,
  approval,
  studyMetadataOptions,
  projectMetadataOptions,
  reviewTemplates,
}) => {
  const formMethods = useForm<GeneralFormData>({
    defaultValues: {
      name: approval.name ?? '',
      study_type: approval.study_type ?? Type.all,
      match_type: approval.match_type ?? matchType.any,
      conditions: approval.conditions ?? [],
      dependencies: _notNil(approval.dependencies) && approval.dependencies !== '' ? Number(approval.dependencies) : '',
    },
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = formMethods;

  const {
    fields: conditions,
    append: appendToConditions,
    remove: removeFromConditions,
  } = useFieldArray({ control, name: 'conditions' });

  const handleAddConditionClick = () => appendToConditions({});
  const handleRemoveConditionClick = (index: number) => removeFromConditions(index);

  const otherReviewTemplates = (): Array<ReviewTemplateItem> =>
    reviewTemplates.filter((template) => Number(approval.id) !== Number(template.id));

  const onSubmitClick = (data: GeneralFormData) => {
    onSubmit({ ...data, conditions: data.conditions ?? [] });
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmitClick)}
        className="ui-card pa3"
        data-test-component="General"
        data-test-element="form"
      >
        <div className="mb3">
          <label htmlFor="general-review-name">Review name</label>
          <input
            id="general-review-name"
            type="text"
            data-test-element="name-input"
            aria-invalid={_notNil(errors.name)}
            style={{ marginBottom: 0 }}
            className={cn({ input__error: _notNil(errors.name) })}
            {...register('name', { required: 'Review name is required' })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }: { message: string }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>

        <fieldset className="bn pa0 ma0 mb3">
          <legend className="dib mb3">Review applies to:</legend>
          {studyTypeOptions.map((t) => (
            <div className="flex items-center pb2" key={t.value}>
              <input
                id={`study_type.${t.value}`}
                type="radio"
                data-test-element="type-radio"
                data-test-key={t.value}
                value={t.value}
                className="pointer ui__form__radio"
                style={{ marginBottom: 0 }}
                {...register('study_type')}
              />
              <label className="pointer pl2" style={{ marginBottom: 0 }} htmlFor={`study_type.${t.value}`}>
                {t.name}
              </label>
            </div>
          ))}
        </fieldset>

        {_isNotEmpty(reviewTemplates) && (
          <div className="mb3">
            <label htmlFor="general-dependencies">Review dependency</label>
            <p className="lt-copy f6 pb2">
              This review can&apos;t be submitted until the selected review template has been completed
            </p>
            <select
              id="general-dependencies"
              data-test-element="dependencies-select"
              data-testid="dependencies-select"
              style={{ width: 300 }}
              {...register('dependencies', {
                setValueAs: (value: string) => (value === '' ? undefined : Number(value)),
              })}
            >
              <option value="">No dependency</option>
              {otherReviewTemplates().map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="pb3">
          <h3 className="lh-title normal f6">Metadata conditions</h3>
          <p className="lh-copy f6 ">
            Trigger reviews based on text and select metadata values entered during study creation.
          </p>
        </div>

        {conditions.length > 1 && (
          <div className="pb3">
            <div className="flex flex-wrap">
              <Radio
                options={[
                  { value: matchType.all, label: 'All conditions' },
                  { value: matchType.any, label: 'Any condition' },
                ]}
                label="Studies must match"
                name="match_type"
              />
            </div>
          </div>
        )}

        <DraggableList
          canAmend
          canAdd
          canRemove
          data={conditions}
          renderItem={asListItem(MetadataCondition, {
            studyMetadataOptions,
            projectMetadataOptions,
          })}
          onAddClick={handleAddConditionClick}
          onRemoveClick={handleRemoveConditionClick}
          addButtonText="Add condition"
          isRemoveButtonFluid={false}
        />

        {cloneElement(ActionButtons, { onOk: handleSubmit(onSubmitClick) })}
      </form>
    </FormProvider>
  );
};

export default General;
